import type { RootState } from "@/app/store/store";
import { useState } from "react";
import { useSelector } from "react-redux";
import type { PersistPartial } from "redux-persist/es/persistReducer";
import { usePoints } from "../app/context/PointsContext";
import {
	useAwardPointsForActivityMutation,
	useCompleteActivityMutation,
} from "../entities/Activity/activityApi";
import {
	useGetUserByIdQuery,
	useGetUserPointsQuery,
} from "../entities/User/userApi";

type ActivityCompletionHook = {
	triggerActivityCompletion: (
		activityId: string,
		pointsToAdd: number,
		resetActivity?: () => void,
	) => Promise<void>;
	isLoading: boolean;
};

export const useActivityCompletion = (): ActivityCompletionHook => {
	const { addPoints, setPoints } = usePoints();
	const [awardPointsForActivity] = useAwardPointsForActivityMutation();
	const [completeActivity] = useCompleteActivityMutation();
	const authStore = useSelector(
		(
			state: RootState &
				PersistPartial & {
					auth: {
						isAuthenticated: boolean;
						user: {
							userId: string;
						};
					};
				},
		) => state.auth,
	);
	const { refetch } = useGetUserPointsQuery(authStore?.user?.userId as string, {
		skip: !authStore?.isAuthenticated,
	});

	const { refetch: refetchUser } = useGetUserByIdQuery(authStore.user.userId, {
		skip: !authStore.isAuthenticated,
	});

	const [isLoading, setIsLoading] = useState(false);

	const triggerActivityCompletion = async (
		activityId: string,
		pointsToAdd: number,
		resetActivity?: () => void,
	) => {
		if (!authStore?.isAuthenticated) return;

		try {
			setIsLoading(true);

			addPoints(pointsToAdd);

			await awardPointsForActivity({
				userId: authStore?.user?.userId as string,
				points: pointsToAdd,
			});

			await completeActivity({
				changes: {
					activityId,
					userId: authStore?.user?.userId as string,
				},
			});

			await refetch();
			await refetchUser();
		} catch (error) {
			console.error("Error completing activity:", error);
		} finally {
			if (resetActivity) resetActivity();
			setPoints(0);

			setIsLoading(false);
		}
	};

	return { triggerActivityCompletion, isLoading };
};
